import { Country } from '@/lib/enums/Country'
import { importGoogleMapsLibrary } from '@/lib/google-maps-loader'
import { useCallback, useRef, useState } from 'react'

/** Google's address components, keyed by their first type; these are the ones callers read. */
export interface PlaceAddressComponents {
  street_number?: string | null
  route?: string | null
  subpremise?: string | null
  locality?: string | null
  neighborhood?: string | null
  administrative_area_level_1?: string | null
  postal_code?: string | null
  postal_code_suffix?: string | null
  country?: string | null
}

/** A picked suggestion's details; empty when they could not be loaded. */
export interface PlaceDetails extends PlaceAddressComponents {
  placeId?: string
  formattedAddress?: string
  name?: string
  types?: string[]
  lat?: number
  lng?: number
}

/** An address the geocoder found. */
export interface GeocodedAddress {
  placeId: string
  formattedAddress: string
  street_number: string
  route: string
  subpremise: string
  locality: string
  neighborhood: string
  administrative_area_level_1: string
  postal_code: string
  postal_code_suffix: string | null
  country: string
  locationType: string
  partialMatch: boolean
  lat?: number
  lng?: number
}

export interface PlaceSuggestion {
  id: string
  name: string
  types: string[]
  value: PlacePrediction
}

export interface PlacesAutocomplete {
  fetchSuggestions: (input: string, options?: AutocompleteOptions) => Promise<PlaceSuggestion[]>
  geocodeLatLng: (point: LatLngInput) => Promise<GeocodedAddress | null>
  getPlaceDetails: (prediction: PlacePrediction) => Promise<PlaceDetails>
  /** The map area that frames a suggested place (a ZIP, a city, a state), or null when Google has none. */
  getPlaceViewport: (prediction: PlacePrediction) => Promise<google.maps.LatLngBounds | null>
  resetSession: () => void
  geocodeAddress: (addressText: string) => Promise<GeocodedAddress | null>
  geocodePlaceId: (placeId: string) => Promise<GeocodedAddress | null>
  isLoaded: boolean
  error: Error | null
}

type PlacePrediction = google.maps.places.PlacePrediction
type AutocompleteOptions = Partial<
  Omit<google.maps.places.AutocompleteRequest, 'input' | 'sessionToken'>
>
type LatLngInput = { lat: number | string; lng: number | string }

export const addressNameFormat: Record<string, 'shortText' | 'longText'> = {
  street_number: 'shortText',
  route: 'longText',
  locality: 'longText',
  administrative_area_level_1: 'shortText',
  country: 'shortText',
  postal_code: 'shortText',
  postal_code_suffix: 'shortText',
  subpremise: 'longText',
}

const includedRegionCodes = [Country.UnitedStates.Value, Country.Canada.Value]

let placesLibraryPromise: Promise<google.maps.PlacesLibrary> | null = null
let geocodingLibraryPromise: Promise<google.maps.GeocodingLibrary> | null = null

async function ensurePlacesLibrary(): Promise<google.maps.PlacesLibrary> {
  if (typeof window === 'undefined') {
    throw new Error('Google Maps JS API can only be used in the browser')
  }

  if (!placesLibraryPromise) {
    // First time: actually load the places library and cache the promise
    placesLibraryPromise = importGoogleMapsLibrary('places')
  }

  return placesLibraryPromise
}

async function ensureGeocodingLibrary(): Promise<google.maps.GeocodingLibrary> {
  if (typeof window === 'undefined') {
    throw new Error('Google Maps JS API can only be used in the browser')
  }

  if (!geocodingLibraryPromise) {
    geocodingLibraryPromise = importGoogleMapsLibrary('geocoding')
  }

  return geocodingLibraryPromise
}

function parseGeocoderResult(result: google.maps.GeocoderResult | undefined): GeocodedAddress {
  const componentMap = new Map<string, string>()
  if (result?.address_components) {
    result.address_components.forEach((component) => {
      const type = component.types?.[0]
      if (!type) return
      // Use long_name by default, except for some short formats
      const value =
        type === 'administrative_area_level_1' ||
        type === 'country' ||
        type === 'postal_code' ||
        type === 'postal_code_suffix' ||
        type === 'street_number'
          ? component.short_name
          : component.long_name

      if (!componentMap.has(type)) {
        componentMap.set(type, value || '')
      }
    })
  }

  const get = (type: string) => componentMap.get(type) || ''

  // Some regions use neighborhood in place of locality; match how our admin form maps fields.
  const locality = get('locality') || get('sublocality') || get('sublocality_level_1') || ''
  const neighborhood = get('neighborhood') || ''

  const lat = result?.geometry?.location?.lat?.()
  const lng = result?.geometry?.location?.lng?.()
  const locationType = result?.geometry?.location_type || ''
  const partialMatch = Boolean(result?.partial_match)

  return {
    placeId: result?.place_id || '',
    formattedAddress: result?.formatted_address || '',
    street_number: get('street_number'),
    route: get('route'),
    subpremise: get('subpremise'),
    locality,
    neighborhood,
    administrative_area_level_1: get('administrative_area_level_1'),
    postal_code: get('postal_code'),
    postal_code_suffix:
      !partialMatch && /^[0-9]{4}$/.test(get('postal_code_suffix'))
        ? get('postal_code_suffix')
        : null,
    country: get('country') || '',
    locationType,
    partialMatch,
    ...(lat !== undefined ? { lat } : {}),
    ...(lng !== undefined ? { lng } : {}),
  }
}

export function usePlacesAutocomplete(): PlacesAutocomplete {
  const [isLoaded, setIsLoaded] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null)

  const resetSession = useCallback(() => {
    sessionTokenRef.current = null
  }, [])

  const fetchSuggestions = useCallback(async (input: string, options: AutocompleteOptions = {}) => {
    if (!input || input.length < 3) {
      return []
    }

    try {
      const placesLibrary = await ensurePlacesLibrary()
      setIsLoaded(true)

      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new placesLibrary.AutocompleteSessionToken()
      }

      const request = {
        input,
        includedRegionCodes,
        ...options,
        sessionToken: sessionTokenRef.current,
      }

      const { suggestions } =
        await placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)

      return suggestions.map(({ placePrediction }) => {
        if (!placePrediction) {
          throw new Error('Google returned an autocomplete suggestion without a place')
        }

        return {
          id: placePrediction.placeId,
          name: placePrediction.text.text,
          types: placePrediction.types ?? [],
          value: placePrediction,
        }
      })
    } catch (err) {
      const normalizedError = err instanceof Error ? err : new Error(String(err))
      setError(normalizedError)
      return []
    }
  }, [])

  const getPlaceDetails = useCallback(async (prediction: PlacePrediction) => {
    // The prediction retains its session token for fetchFields. Clear our reference now so
    // any new typing that happens while details load starts a fresh billing session.
    sessionTokenRef.current = null

    try {
      // Ensure the library is loaded before using prediction.toPlace()
      await ensurePlacesLibrary()
      setIsLoaded(true)

      const { place } = await prediction.toPlace().fetchFields({
        fields: ['addressComponents', 'formattedAddress', 'location'],
      })

      if (!place.addressComponents) {
        throw new Error('Google returned a place without address components')
      }

      const addressComponents = place.addressComponents.reduce<Record<string, string | null>>(
        (acc, component) => {
          const type = component.types[0]
          if (!type) return acc
          const format = addressNameFormat[type]

          acc[type] = format === 'shortText' ? component.shortText : component.longText

          return acc
        },
        {}
      )
      const postalCodeSuffix = addressComponents.postal_code_suffix

      return {
        ...addressComponents,
        postal_code_suffix:
          postalCodeSuffix && /^[0-9]{4}$/.test(postalCodeSuffix) ? postalCodeSuffix : null,
        // Include place id so callers can persist verification metadata
        ...(prediction?.placeId ? { placeId: prediction.placeId } : {}),
        ...(place.formattedAddress ? { formattedAddress: place.formattedAddress } : {}),
        name: prediction?.mainText?.text || '',
        types: prediction?.types ?? [],
        // Add coordinates if available
        ...(place.location ? { lat: place.location.lat(), lng: place.location.lng() } : {}),
      }
    } catch (err) {
      const normalizedError = err instanceof Error ? err : new Error(String(err))
      setError(normalizedError)
      return {}
    }
  }, [])

  const getPlaceViewport = useCallback(async (prediction: PlacePrediction) => {
    // Picking a suggestion ends its billing session, as in getPlaceDetails.
    sessionTokenRef.current = null

    try {
      await ensurePlacesLibrary()
      const { place } = await prediction.toPlace().fetchFields({ fields: ['viewport'] })

      return place.viewport ?? null
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)))
      return null
    }
  }, [])

  const geocodeAddress = useCallback(async (addressText: string) => {
    try {
      if (!addressText || String(addressText).trim().length < 5) {
        return null
      }

      await ensureGeocodingLibrary()
      setIsLoaded(true)

      // Prefer the JS API geocoder so we can use the same client key restrictions.
      const geocoder = new window.google.maps.Geocoder()
      const { results } = await geocoder.geocode({
        address: String(addressText).trim(),
        region: Country.UnitedStates.Value,
      })

      if (!results || results.length === 0) {
        return null
      }

      return parseGeocoderResult(results[0])
    } catch (err) {
      const normalizedError = err instanceof Error ? err : new Error(String(err))
      setError(normalizedError)
      return null
    }
  }, [])

  const geocodePlaceId = useCallback(async (placeId: string) => {
    try {
      if (!placeId || String(placeId).trim().length < 5) {
        return null
      }

      await ensureGeocodingLibrary()
      setIsLoaded(true)

      const geocoder = new window.google.maps.Geocoder()
      const { results } = await geocoder.geocode({
        placeId: String(placeId).trim(),
      })

      if (!results || results.length === 0) {
        return null
      }

      return parseGeocoderResult(results[0])
    } catch (err) {
      const normalizedError = err instanceof Error ? err : new Error(String(err))
      setError(normalizedError)
      return null
    }
  }, [])

  const geocodeLatLng = useCallback(async ({ lat, lng }: LatLngInput) => {
    try {
      if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) {
        return null
      }

      await ensureGeocodingLibrary()
      setIsLoaded(true)

      const geocoder = new window.google.maps.Geocoder()
      const { results } = await geocoder.geocode({
        location: {
          lat: Number(lat),
          lng: Number(lng),
        },
      })

      if (!results || results.length === 0) {
        return null
      }

      return parseGeocoderResult(results[0])
    } catch (err) {
      const normalizedError = err instanceof Error ? err : new Error(String(err))
      setError(normalizedError)
      return null
    }
  }, [])

  return {
    fetchSuggestions,
    geocodeLatLng,
    getPlaceDetails,
    getPlaceViewport,
    resetSession,
    geocodeAddress,
    geocodePlaceId,
    isLoaded,
    error,
  }
}
